import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { ArrowLeft, CheckCircle, Loader2, Calendar, Phone, Mail, User, AlertCircle, Plus, Minus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const QurbaniPage = () => {
  const [cart, setCart] = useState([]);
  const [customerInfo, setCustomerInfo] = useState({
    name: "",
    phone: "",
    email: "",
    pickup_date: "",
    special_instructions: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [orderId, setOrderId] = useState(null);

  const animals = [
    {
      id: "lamb",
      name: "Lamb",
      price: 500,
      weight: "40-50 lbs",
      description: "HMS Certified Zabiha",
      minAge: "1+ year old",
      available: true,
      minDays: 1
    },
    {
      id: "goat",
      name: "Goat",
      price: 575,
      weight: "40-50 lbs", 
      description: "HMS Certified Zabiha",
      minAge: "1+ year old",
      available: true,
      minDays: 1
    },
    {
      id: "cow",
      name: "Cow (1/7 Share)",
      price: 425,
      weight: "Shared by 7",
      description: "HMS Certified Zabiha",
      minAge: "2+ years old",
      available: true,
      minDays: 3,
      note: "Pickup: 3rd day"
    }
  ];

  const addToCart = (animal) => {
    const existing = cart.find(item => item.id === animal.id);
    if (existing) {
      setCart(cart.map(item => 
        item.id === animal.id 
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, { ...animal, quantity: 1 }]);
    }
    toast.success(`Added ${animal.name}`);
  };

  const updateQuantity = (animalId, change) => {
    setCart(cart.map(item => {
      if (item.id === animalId) {
        const newQty = item.quantity + change;
        if (newQty <= 0) return null;
        return { ...item, quantity: newQty };
      }
      return item;
    }).filter(Boolean));
  };

  const removeFromCart = (animalId) => {
    setCart(cart.filter(item => item.id !== animalId));
  };

  const getTotal = () => {
    return cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const getMinPickupDays = () => {
    if (cart.length === 0) return 1;
    return Math.max(...cart.map(item => item.minDays));
  };

  const getMinPickupDate = () => {
    const today = new Date();
    const minDays = getMinPickupDays();
    today.setDate(today.getDate() + minDays);
    return today.toISOString().split('T')[0];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (cart.length === 0) {
      toast.error("Please select at least one animal");
      return;
    }

    if (!customerInfo.name || !customerInfo.phone) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    
    try {
      const items = cart.map(item => ({
        product_id: `qurbani-${item.id}`,
        product_name: `Qurbani ${item.name}`,
        quantity: item.quantity,
        unit: "whole",
        piece_size: item.weight,
        notes: `Qurbani Order - ${item.minAge} guaranteed`
      }));

      const orderSummary = cart.map(item => `${item.quantity}x ${item.name}`).join(', ');

      const orderData = {
        customer_name: customerInfo.name,
        customer_phone: customerInfo.phone,
        customer_email: customerInfo.email || "",
        items: items,
        special_instructions: `QURBANI ORDER - Eid Al-Adha\nOrder: ${orderSummary}\nTotal: $${getTotal()}\nPickup: Next Day (Cow: 3rd day)\n${customerInfo.special_instructions}`,
        pickup_date: "",
        order_type: "qurbani"
      };

      const response = await axios.post(`${API}/orders`, orderData);
      setOrderId(response.data.order_id);
      setOrderPlaced(true);
      toast.success("Qurbani order placed!");
    } catch (error) {
      console.error("Order error:", error);
      toast.error("Failed to place order. Please call us.");
    } finally {
      setSubmitting(false);
    }
  };

  if (orderPlaced) {
    return (
      <div className="min-h-screen bg-[#1A1A1A]">
        <div className="max-w-2xl mx-auto px-4 py-12">
          <div className="bg-white rounded-lg p-8 text-center">
            <div className="w-20 h-20 bg-[#1A5E1F] rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-[#1A1A1A] mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
              Qurbani Order Confirmed!
            </h1>
            <p className="text-lg text-[#666] mb-2">Order ID: <span className="font-bold text-[#A61B1B]">#{orderId}</span></p>
            <p className="text-[#666] mb-6">
              JazakAllahu Khairan! We will contact you to confirm pickup.
            </p>
            <div className="space-y-3">
              <Link to={`/track/${orderId}`}>
                <Button className="w-full bg-[#1A5E1F] hover:bg-[#155219]">
                  Track Your Order
                </Button>
              </Link>
              <Link to="/">
                <Button variant="outline" className="w-full">
                  Return Home
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F7F2]">
      {/* Header */}
      <header className="bg-[#1A1A1A] border-b border-[#333] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white hover:text-[#D4AF37] transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm">Back</span>
          </Link>
          <div className="text-center">
            <h1 className="text-white font-bold text-lg" style={{ fontFamily: 'Fraunces, serif' }}>
              Qurbani Orders
            </h1>
            <p className="text-[#D4AF37] text-xs">Eid Al-Adha 2025</p>
          </div>
          <img 
            src="https://www.marketplaceonoakton.com/images/market-place-on-oakton.png" 
            alt="Market Place" 
            className="h-10 w-auto"
          />
        </div>
      </header>

      {/* Quick Info Bar */}
      <div className="bg-[#1A5E1F] text-white py-2 px-4">
        <div className="max-w-5xl mx-auto flex flex-wrap justify-center gap-4 text-sm">
          <span>✓ HMS Certified Zabiha</span>
          <span>✓ Proper Age Animals</span>
          <span>✓ 40-50 lbs Guaranteed</span>
          <span>✓ Pay at Pickup</span>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Main Order Section */}
        <div className="grid lg:grid-cols-3 gap-6">
          
          {/* Left: Animal Selection & Form */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-[#1A1A1A]" style={{ fontFamily: 'Fraunces, serif' }}>
                Select Your Qurbani
              </h2>
              <p className="text-sm text-[#666]">Mix & match allowed</p>
            </div>

            {/* Animal Cards */}
            <div className="grid sm:grid-cols-3 gap-4">
              {animals.map((animal) => {
                const inCart = cart.find(item => item.id === animal.id);
                return (
                  <div
                    key={animal.id}
                    className={`bg-white rounded-lg border-2 p-4 transition-all ${
                      inCart ? 'border-[#1A5E1F] shadow-md' : 'border-[#DCD7CC] hover:border-[#A61B1B]'
                    }`}
                    data-testid={`qurbani-${animal.id}`}
                  >
                    <div className="text-center">
                      <h3 className="font-bold text-[#1A1A1A]">{animal.name}</h3>
                      <p className="text-2xl font-bold text-[#A61B1B] my-1">${animal.price}</p>
                      <p className="text-xs text-[#666]">{animal.weight}</p>
                      <p className="text-xs text-[#1A5E1F] font-medium mt-1">{animal.minAge}</p>
                      {animal.note && (
                        <p className="text-xs text-[#A61B1B] mt-1">{animal.note}</p>
                      )}
                      
                      {inCart ? (
                        <div className="flex items-center justify-center gap-2 mt-3 bg-[#F0FDF4] rounded-lg p-2">
                          <button 
                            onClick={() => updateQuantity(animal.id, -1)}
                            className="bg-white border rounded p-1 hover:bg-red-50"
                          >
                            <Minus className="w-4 h-4 text-[#A61B1B]" />
                          </button>
                          <span className="font-bold w-8 text-center">{inCart.quantity}</span>
                          <button 
                            onClick={() => updateQuantity(animal.id, 1)}
                            className="bg-white border rounded p-1 hover:bg-green-50"
                          >
                            <Plus className="w-4 h-4 text-[#1A5E1F]" />
                          </button>
                        </div>
                      ) : (
                        <Button 
                          onClick={() => addToCart(animal)}
                          className="w-full mt-3 bg-[#1A5E1F] hover:bg-[#155219] h-9"
                          size="sm"
                        >
                          <Plus className="w-4 h-4 mr-1" /> Add
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Your Order Summary */}
            {cart.length > 0 && (
              <div className="bg-white border-2 border-[#1A1A1A] rounded-lg p-4">
                <h3 className="font-bold text-[#1A1A1A] mb-3">Your Order</h3>
                <div className="space-y-2">
                  {cart.map((item) => (
                    <div key={item.id} className="flex items-center justify-between py-2 border-b border-[#EEE]">
                      <div>
                        <span className="font-medium">{item.quantity}x {item.name}</span>
                        <span className="text-xs text-[#666] ml-2">({item.minAge})</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-bold text-[#A61B1B]">${item.price * item.quantity}</span>
                        <button 
                          onClick={() => removeFromCart(item.id)}
                          className="text-[#999] hover:text-[#A61B1B]"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between items-center mt-3 pt-3 border-t-2 border-[#1A1A1A]">
                  <span className="font-bold text-lg">Total:</span>
                  <span className="text-2xl font-bold text-[#A61B1B]">${getTotal()}</span>
                </div>
              </div>
            )}

            {/* Customer Form */}
            <div className="bg-white border-2 border-[#DCD7CC] rounded-lg p-4">
              <h3 className="font-bold text-[#1A1A1A] mb-4">Your Information</h3>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name" className="text-sm font-medium">Full Name *</Label>
                    <Input
                      id="name"
                      value={customerInfo.name}
                      onChange={(e) => setCustomerInfo({...customerInfo, name: e.target.value})}
                      placeholder="Your name"
                      required
                      className="mt-1"
                      data-testid="qurbani-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-sm font-medium">Phone *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={customerInfo.phone}
                      onChange={(e) => setCustomerInfo({...customerInfo, phone: e.target.value})}
                      placeholder="(847) 555-1234"
                      required
                      className="mt-1"
                      data-testid="qurbani-phone"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email" className="text-sm font-medium">Email (Optional)</Label>
                    <Input
                      id="email"
                      type="email"
                      value={customerInfo.email}
                      onChange={(e) => setCustomerInfo({...customerInfo, email: e.target.value})}
                      placeholder="your@email.com"
                      className="mt-1"
                      data-testid="qurbani-email"
                    />
                  </div>
                  <div className="flex items-center">
                    <div className="bg-[#1A5E1F] text-white rounded-lg p-3 w-full text-center">
                      <p className="text-xs text-white/80">Pickup Available</p>
                      <p className="font-bold">Next Day</p>
                      <p className="text-xs text-white/80">(Cow: 3rd day)</p>
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="instructions" className="text-sm font-medium">Cutting Preferences</Label>
                  <Textarea
                    id="instructions"
                    value={customerInfo.special_instructions}
                    onChange={(e) => setCustomerInfo({...customerInfo, special_instructions: e.target.value})}
                    placeholder="Any special cutting instructions..."
                    className="mt-1"
                    rows={2}
                    data-testid="qurbani-instructions"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting || cart.length === 0}
                  className="w-full bg-[#A61B1B] hover:bg-[#8B1515] text-white py-6 text-lg font-bold"
                  data-testid="qurbani-submit"
                >
                  {submitting ? (
                    <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Placing Order...</>
                  ) : cart.length === 0 ? (
                    "Select Animals to Order"
                  ) : (
                    `Place Order — $${getTotal()}`
                  )}
                </Button>

                <p className="text-center text-sm text-[#666]">
                  💵 No payment online — Pay when you pick up
                </p>
              </form>
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="space-y-4">
            {/* Contact Card */}
            <div className="bg-[#1A1A1A] text-white rounded-lg p-4 text-center">
              <p className="text-sm text-[#999]">Questions? Call us</p>
              <p className="text-2xl font-bold text-[#D4AF37]">847-652-0682</p>
              <p className="text-xs text-[#666] mt-1">Market Place on Oakton</p>
            </div>

            {/* Our Promise */}
            <div className="bg-[#1A5E1F] text-white rounded-lg p-4">
              <h4 className="font-bold mb-3">Our Promise</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>Proper age animals — no babies</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>40-50 lbs guaranteed weight</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>HMS certified Zabiha Halal</span>
                </li>
              </ul>
            </div>

            {/* Location */}
            <div className="bg-white border-2 border-[#DCD7CC] rounded-lg p-4 text-center">
              <p className="font-bold text-[#1A1A1A]">Pickup Location</p>
              <p className="text-sm text-[#666] mt-1">
                4817 W Oakton St<br />
                Skokie, IL 60077
              </p>
            </div>
          </div>
        </div>

        {/* HADITH & WHY WE'RE DIFFERENT SECTION - Below the order form */}
        <div className="mt-10 space-y-6">
          
          {/* Warning Banner */}
          <div className="bg-[#A61B1B] text-white rounded-lg p-5">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-8 h-8 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Fraunces, serif' }}>
                  Why Our Prices May Be Higher Than Others
                </h3>
                <p className="text-white/90 leading-relaxed">
                  Many shops offer <strong>cheaper Qurbani</strong> — but they use <strong>baby animals</strong> that are 
                  too young. According to Islamic law, young animals <strong>do not count as valid Qurbani</strong>. 
                  Your sacrifice may not be accepted if the animal doesn't meet the age requirement.
                </p>
                <p className="text-white/90 mt-3">
                  <strong>We charge more because we only use proper-aged, full-sized animals.</strong> You're not just 
                  paying for meat — you're ensuring your Qurbani is valid and accepted, InshaAllah.
                </p>
              </div>
            </div>
          </div>

          {/* Hadith Section */}
          <div className="bg-white border-2 border-[#D4AF37] rounded-lg p-6">
            <div className="text-center mb-5">
              <span className="text-[#D4AF37] text-4xl">☪</span>
              <h3 className="text-xl font-bold text-[#1A1A1A] mt-2" style={{ fontFamily: 'Fraunces, serif' }}>
                What Does Islam Say?
              </h3>
            </div>

            {/* Hadith Quote */}
            <div className="bg-[#FEF3C7] border border-[#F59E0B] rounded-lg p-5 mb-5">
              <p className="text-lg text-[#78350F] text-center leading-relaxed mb-3">
                The Prophet Muhammad ﷺ said:
              </p>
              <p className="text-xl text-[#1A1A1A] text-center font-bold italic mb-3">
                "Do not sacrifice except a mature animal (Musinnah)."
              </p>
              <p className="text-center text-[#A61B1B] font-semibold">— Sahih Muslim</p>
            </div>

            {/* Simple Explanation */}
            <div className="mb-5">
              <h4 className="font-bold text-[#1A1A1A] mb-3">What This Means:</h4>
              <p className="text-[#666] leading-relaxed mb-3">
                <strong>"Musinnah"</strong> means a mature, fully-grown animal — not a baby or young animal. 
                The Prophet ﷺ specifically told us to only sacrifice animals that have reached proper maturity.
              </p>
              <p className="text-[#666] leading-relaxed">
                <strong>Why does this matter?</strong> Qurbani is a sacred act of worship to Allah. Using an underage 
                animal means the sacrifice may not be valid. Many butchers sell younger animals at cheaper prices, 
                but this defeats the entire purpose of your Qurbani.
              </p>
            </div>

            {/* Age Requirements */}
            <div className="border-t border-[#DCD7CC] pt-5">
              <h4 className="font-bold text-[#1A1A1A] mb-4 text-center">Minimum Age for Valid Qurbani</h4>
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="bg-[#F9F7F2] p-4 rounded-lg text-center">
                  <p className="text-2xl mb-2">🐑</p>
                  <p className="font-bold text-[#1A1A1A]">Sheep / Lamb</p>
                  <p className="text-xl text-[#1A5E1F] font-bold mt-1">1 Year Old</p>
                  <p className="text-xs text-[#666] mt-1">Must have adult teeth</p>
                </div>
                <div className="bg-[#F9F7F2] p-4 rounded-lg text-center">
                  <p className="text-2xl mb-2">🐐</p>
                  <p className="font-bold text-[#1A1A1A]">Goat</p>
                  <p className="text-xl text-[#1A5E1F] font-bold mt-1">1 Year Old</p>
                  <p className="text-xs text-[#666] mt-1">Fully mature goat</p>
                </div>
                <div className="bg-[#F9F7F2] p-4 rounded-lg text-center">
                  <p className="text-2xl mb-2">🐄</p>
                  <p className="font-bold text-[#1A1A1A]">Cow</p>
                  <p className="text-xl text-[#1A5E1F] font-bold mt-1">2 Years Old</p>
                  <p className="text-xs text-[#666] mt-1">Can share with 7 people</p>
                </div>
              </div>
            </div>
          </div>

          {/* Comparison Table */}
          <div className="bg-white border-2 border-[#DCD7CC] rounded-lg p-6">
            <h3 className="text-xl font-bold text-[#1A1A1A] mb-4 text-center" style={{ fontFamily: 'Fraunces, serif' }}>
              Cheap Qurbani vs. Proper Qurbani
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {/* Cheap Option */}
              <div className="bg-[#FEE2E2] border-2 border-[#EF4444] rounded-lg p-4">
                <h4 className="font-bold text-[#991B1B] mb-3 flex items-center gap-2">
                  <span className="text-xl">✗</span> Other Shops (Cheaper)
                </h4>
                <ul className="space-y-2 text-sm text-[#7F1D1D]">
                  <li>• Baby goats/lambs (3-6 months old)</li>
                  <li>• Small animals (20-30 lbs)</li>
                  <li>• May NOT be valid Qurbani</li>
                  <li>• Lower price, but defeats the purpose</li>
                </ul>
              </div>
              
              {/* Our Option */}
              <div className="bg-[#D1FAE5] border-2 border-[#10B981] rounded-lg p-4">
                <h4 className="font-bold text-[#065F46] mb-3 flex items-center gap-2">
                  <span className="text-xl">✓</span> Market Place on Oakton
                </h4>
                <ul className="space-y-2 text-sm text-[#064E3B]">
                  <li>• Mature animals (1-2 years old)</li>
                  <li>• Full-sized (40-50 lbs guaranteed)</li>
                  <li>• Valid Qurbani, InshaAllah</li>
                  <li>• HMS Certified Zabiha Halal</li>
                </ul>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#1A1A1A] py-6 px-4 text-center mt-10">
        <p className="text-[#D4AF37] font-bold text-lg mb-2">Market Place on Oakton</p>
        <p className="text-[#999] text-sm">4817 W Oakton St, Skokie, IL 60077</p>
        <p className="text-[#D4AF37] text-lg font-bold mt-2">847-652-0682</p>
      </footer>
    </div>
  );
};

export default QurbaniPage;
